
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { format } from 'date-fns';
import { CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Resource, ResourceAssignment } from "../hooks/useResourceManagement";
import { ProjectTask } from "../types";
import { toast } from "sonner";

interface ResourceAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (assignment: Omit<ResourceAssignment, 'id' | 'user_id'>) => Promise<boolean>;
  resources: Resource[];
  tasks: ProjectTask[];
  assignment?: ResourceAssignment | null;
  isEdit?: boolean;
  projectId: string | null;
}

const ResourceAssignmentDialog = ({
  open,
  onOpenChange,
  onSave,
  resources,
  tasks,
  assignment,
  isEdit = false,
  projectId
}: ResourceAssignmentDialogProps) => {
  const [taskId, setTaskId] = useState<string | undefined>(assignment?.task_id);
  const [resourceId, setResourceId] = useState<string | undefined>(assignment?.resource_id);
  const [startDate, setStartDate] = useState<Date | undefined>(assignment?.start_date ? new Date(assignment.start_date) : undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(assignment?.end_date ? new Date(assignment.end_date) : undefined);
  const [allocationHrs, setAllocationHrs] = useState<string | undefined>(assignment?.allocation_hrs || '');
  const [allocatedPercentage, setAllocatedPercentage] = useState<number | undefined>(assignment?.allocated_percentage);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (assignment) {
      setTaskId(assignment.task_id);
      setResourceId(assignment.resource_id);
      setStartDate(assignment.start_date ? new Date(assignment.start_date) : undefined);
      setEndDate(assignment.end_date ? new Date(assignment.end_date) : undefined);
      setAllocationHrs(assignment.allocation_hrs || '');
      setAllocatedPercentage(assignment.allocated_percentage);
    } else {
      // Reset the form when the dialog is opened for a new assignment
      setTaskId(undefined);
      setResourceId(undefined);
      setStartDate(undefined);
      setEndDate(undefined);
      setAllocationHrs('');
      setAllocatedPercentage(undefined);
    }
  }, [assignment]);

  const handleSave = async () => {
    if (!taskId || !resourceId || !startDate || !endDate) {
      toast.error('Please fill in all fields.');
      return;
    }

    setIsSaving(true);
    
    if (!projectId) {
      toast.error('Project ID is required');
      setIsSaving(false);
      return;
    }

    const newAssignment = {
      task_id: taskId,
      resource_id: resourceId,
      start_date: format(startDate, 'yyyy-MM-dd'),
      end_date: format(endDate, 'yyyy-MM-dd'),
      allocation_hrs: allocationHrs || null,
      allocated_percentage: allocatedPercentage !== undefined ? allocatedPercentage : null,
      utilization_percentage: null,
      project_id: projectId
    };

    const success = await onSave(newAssignment);

    if (success) {
      onOpenChange(false);
    }

    setIsSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white text-black border-primary/20">
        <DialogHeader>
          <DialogTitle className="text-black">{assignment ? 'Edit Assignment' : 'Add Assignment'}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Task Selection */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="task" className="text-right">
              Task
            </Label>
            <Select onValueChange={setTaskId} value={taskId}>
              <SelectTrigger className="w-full col-span-3">
                <SelectValue placeholder="Select a task" />
              </SelectTrigger>
              <SelectContent>
                {tasks.map((task) => (
                  <SelectItem key={task.id} value={task.id}>
                    {task.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Resource Selection */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="resource" className="text-right">
              Resource
            </Label>
            <Select onValueChange={setResourceId} value={resourceId}>
              <SelectTrigger className="w-full col-span-3">
                <SelectValue placeholder="Select a resource" />
              </SelectTrigger>
              <SelectContent>
                {resources.map((resource) => (
                  <SelectItem key={resource.id} value={resource.id}>
                    {resource.name} ({resource.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Start Date */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="start-date" className="text-right">
              Start Date
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={"outline"}
                  className="w-[280px] justify-start text-left font-normal col-span-3 bg-white text-black border-gray-300"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, "dd MMM yyyy") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar
                  mode="single"
                  weekStartsOn={0}
                  selected={startDate}
                  onSelect={setStartDate}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* End Date */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="end-date" className="text-right">
              End Date
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={"outline"}
                  className="w-[280px] justify-start text-left font-normal col-span-3 bg-white text-black border-gray-300"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {endDate ? format(endDate, "dd MMM yyyy") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar
                  mode="single"
                  weekStartsOn={0}
                  selected={endDate}
                  onSelect={setEndDate}
                  disabled={(date) =>
                    date && startDate && date < startDate
                  }
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Allocation Hours */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="allocation-hrs" className="text-right">
              Allocation (Hours)
            </Label>
            <Input
              id="allocation-hrs"
              value={allocationHrs || ''}
              onChange={(e) => setAllocationHrs(e.target.value)}
              className="col-span-3"
            />
          </div>

          {/* Allocated Percentage */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="allocated-percentage" className="text-right">
              Allocation (%)
            </Label>
            <Input
              type="number"
              id="allocated-percentage"
              value={allocatedPercentage !== undefined ? allocatedPercentage.toString() : ''}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                setAllocatedPercentage(isNaN(value) ? undefined : value);
              }}
              className="col-span-3"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" className="border-gray-300 bg-gray-100 text-black hover:bg-gray-200" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" className="bg-primary text-black hover:bg-primary/80" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ResourceAssignmentDialog;
